/**
 * <curtain-footer>
 *
 * Theater-curtain footer. The reveal itself is pure CSS: the element stays in
 * normal flow, `position: sticky; bottom: 0; z-index: -1`, and <main> is a
 * raised opaque layer that lifts away to expose it. Nothing is measured, so
 * the effect is identical on every device and immune to mobile browser bars.
 *
 * This script only guarantees reachability: while the footer is still hidden
 * behind <main>, a keyboard focus or a hash landing inside it scrolls the
 * page so the focused element is actually exposed.
 *
 * Lifecycle: listeners carry this connection's AbortSignal, recreated per
 * connect; disconnectedCallback aborts them all.
 */
class CurtainFooter extends HTMLElement {
  /** @type {AbortController | null} */
  #lifetime = null;

  connectedCallback() {
    if (window.Shopify?.designMode) return;

    this.#lifetime?.abort();
    this.#lifetime = new AbortController();
    const { signal } = this.#lifetime;

    this.addEventListener('focusin', this.#reveal, { signal });
    window.addEventListener('hashchange', this.#revealIfHashInside, { signal });
    if (location.hash) this.#revealIfHashInside();
  }

  disconnectedCallback() {
    this.#lifetime?.abort();
    this.#lifetime = null;
  }

  /** Scroll so the end of main has passed above the focused element. */
  #reveal = (event) => {
    const target = event?.target instanceof HTMLElement ? event.target : this;
    const sentinel = document.querySelector('.tg-curtain__sentinel');
    if (!(sentinel instanceof HTMLElement)) return;

    if (sentinel.getBoundingClientRect().bottom <= target.getBoundingClientRect().top) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  };

  #revealIfHashInside = () => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;

    const target = document.getElementById(id);
    if (target && this.contains(target)) this.#reveal({ target });
  };
}

if (!customElements.get('curtain-footer')) {
  customElements.define('curtain-footer', CurtainFooter);
}
